import React from "react";
import { Target, Zap, Smartphone, Stethoscope, MessageCircle, ShieldCheck } from "lucide-react";

const REASONS = [
  { icon: Target, title: "Real leads, not just design", body: "Every block is tested for one job — getting calls, WhatsApp pings, and bookings." },
  { icon: Zap, title: "Fast delivery — 48 hours", body: "From WhatsApp ping to live page in 2 days. No 'we'll get back next month'." },
  { icon: Smartphone, title: "Mobile-first conversion", body: "Designed for the 80% who arrive on phones. Big tap targets, no clutter." },
  { icon: Stethoscope, title: "Built for doctors & locals", body: "We speak the language of clinics, salons, gyms, and small businesses in India." },
  { icon: MessageCircle, title: "Easy WhatsApp comms", body: "No agency jargon, no calls to schedule. Talk to us where you already work." },
  { icon: ShieldCheck, title: "No lock-in, no surprises", body: "Transparent pricing, ownership of your page, and zero hidden fees." },
];

const TRUST_IMG = "https://images.pexels.com/photos/36454696/pexels-photo-36454696.jpeg";

export default function WhyUs() {
  return (
    <section data-testid="why-us-section" className="py-20 lg:py-28 bg-white border-y border-[var(--gf-border)]">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-12 gap-12 items-start">
        <div className="lg:col-span-5">
          <p className="text-xs uppercase tracking-[0.22em] font-bold text-[var(--gf-blue)]">Why GrowFlow</p>
          <h2 className="font-display mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-[1.08]">
            Built like a growth team, priced like a freelancer.
          </h2>
          <p className="mt-5 text-lg text-[var(--gf-text-2)]">
            We're a small, focused team obsessed with one outcome: filling your appointment book. We won't oversell you,
            and we won't disappear after delivery.
          </p>

          <div className="mt-7 relative rounded-3xl overflow-hidden border border-[var(--gf-border)] gf-shadow-soft">
            <img src={TRUST_IMG} alt="Confident clinic owner" className="w-full h-72 object-cover" loading="lazy" />
            <div className="absolute inset-0 bg-gradient-to-t from-[var(--gf-primary)]/75 via-transparent to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
              <p className="text-sm italic leading-relaxed">
                "Within a week of going live, I started getting 5–7 WhatsApp inquiries every day. The page just works."
              </p>
              <p className="mt-3 text-xs font-semibold opacity-90">— Dr. Anjali R., Dental Clinic, Pune</p>
            </div>
          </div>
        </div>

        <div className="lg:col-span-7 grid sm:grid-cols-2 gap-5">
          {REASONS.map((r, i) => (
            <div
              key={i}
              data-testid={`why-card-${i}`}
              className="rounded-2xl border border-[var(--gf-border)] p-6 hover:bg-[var(--gf-bg)] hover:-translate-y-1 transition-all"
            >
              <div className="h-11 w-11 rounded-xl bg-[var(--gf-blue-soft)] text-[var(--gf-blue)] flex items-center justify-center">
                <r.icon className="h-5 w-5" />
              </div>
              <h3 className="font-display mt-4 text-base font-bold">{r.title}</h3>
              <p className="text-sm text-[var(--gf-text-2)] mt-2 leading-relaxed">{r.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
